import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { bookingService } from '@/api/services/bookingService'

type BLP = Parameters<typeof bookingService.list>[0]

export function useBookings(p: BLP) {
  return useQuery({ queryKey: ['bookings', 'list', p], queryFn: () => bookingService.list(p), staleTime: 30_000 })
}

// Just the PENDING total — used for the sidebar's "Bookings" badge, and by the
// Booking List page to mark that count as "seen" once the admin visits it.
export function usePendingBookingsCount(enabled = true) {
  return useQuery({
    queryKey: ['bookings', 'pending-count'],
    queryFn: () => bookingService.list({ booking_status: 'PENDING', page: 1, limit: 1 }),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    select: (data: any) => data?.total ?? data?.meta?.total ?? data?.data?.total_count ?? data?.total_count ?? 0,
  })
}

export function useBooking(id: number) {
  return useQuery({ queryKey: ['bookings', id], queryFn: () => bookingService.getById(id), enabled: !!id })
}

export function useRefunds(p?: Parameters<typeof bookingService.refundList>[0]) {
  return useQuery({ queryKey: ['refunds', 'list', p], queryFn: () => bookingService.refundList(p), staleTime: 30_000 })
}

// Already-refunded list (Refunded Requests tab)
export function useRefundedList(p?: Parameters<typeof bookingService.refundedList>[0]) {
  return useQuery({ queryKey: ['refunds', 'refunded', p], queryFn: () => bookingService.refundedList(p), staleTime: 30_000 })
}

// Refund details (commission / fee breakdown) for a booking
export function useRefundDetails(bookingId: number) {
  return useQuery({
    queryKey: ['refunds', 'details', bookingId],
    queryFn: () => bookingService.refundById(bookingId),
    enabled: !!bookingId,
  })
}

// Create a new refund request { booking_id, reason }
export function useCreateRefund() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => bookingService.createRefund(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['refunds'] }),
  })
}

// Process the refund amount { booking_id, amount_refunded } (update-status-by-booking-id)
export function useProcessRefund() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => bookingService.updateRefundStatus(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['refunds'] }),
  })
}

// Change refund request status { booking_id, status } (update-refund-request)
export function useUpdateRefundRequestStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => bookingService.updateRefundRequest(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['refunds'] }),
  })
}

// Accept/reject a session request on behalf of the trainer { sessions_id, session_status, driver_comments? }
export function useUpdateSessionStatusOnBehalf() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => bookingService.updateSessionStatusOnBehalf(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['booking-session-requests'] }),
  })
}

export function useTrainerSwapHistory(p?: Parameters<typeof bookingService.trainerSwapHistory>[0]) {
  return useQuery({ queryKey: ['bookings', 'trainer-swap-history', p], queryFn: () => bookingService.trainerSwapHistory(p), staleTime: 30_000 })
}

// Reassign a booking to a new trainer { booking_id, new_trainer_id, reason }
export function useChangeTrainer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => bookingService.changeTrainer(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['booking-detail'] }),
  })
}

// Edit a single session's schedule/status { session_id, start_time, end_time, session_status, reason }
export function useEditSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => bookingService.editSession(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['booking-detail'] })
      qc.invalidateQueries({ queryKey: ['bookings'] })
    },
  })
}

// Audit log of past edits to one session
export function useSessionEditHistory(sessionId?: number, enabled = true) {
  return useQuery({
    queryKey: ['session-edit-history', sessionId],
    queryFn: () => bookingService.sessionEditHistory(Number(sessionId)),
    enabled: enabled && !!sessionId,
    retry: false,
  })
}

// Session status enum (numeric id ↔ name) — used by the edit-session modal
export function useSessionStatusEnum(enabled = true) {
  return useQuery({
    queryKey: ['enum', 'session-status'],
    queryFn: () => bookingService.sessionStatusEnum(),
    enabled,
    staleTime: 60 * 60_000,
    retry: false,
  })
}

// Payment status enum (numeric value ↔ name)
export function usePaymentStatusEnum(enabled = true) {
  return useQuery({
    queryKey: ['enum', 'payment-status'],
    queryFn: () => bookingService.paymentStatusEnum(),
    enabled,
    staleTime: 60 * 60_000,
    retry: false,
  })
}

export function useUpdateBookingStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => bookingService.updateStatus(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookings'] }),
  })
}

export function useApproveRefund() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => bookingService.updateRefundStatus(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['refunds'] }),
  })
}

export function useRejectRefund() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => bookingService.updateRefundRequest(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['refunds'] }),
  })
}

// alias kept for pages that use useCancelBooking
export const useCancelBooking = useUpdateBookingStatus

// ── Auto-reassign settings ──────────────────────────────────────────────────
export function useAutoReassignSettings() {
  return useQuery({ queryKey: ['auto-reassign-settings'], queryFn: () => bookingService.getAutoReassignSettings(), staleTime: 30_000 })
}
export function useUpdateAutoReassignSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof bookingService.updateAutoReassignSettings>[0]) => bookingService.updateAutoReassignSettings(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auto-reassign-settings'] }),
  })
}

// ── Auto-reassign issues ─────────────────────────────────────────────────────
export function useAutoReassignIssues(p?: Parameters<typeof bookingService.listAutoReassignIssues>[0]) {
  return useQuery({ queryKey: ['auto-reassign-issues', 'list', p], queryFn: () => bookingService.listAutoReassignIssues(p), staleTime: 15_000 })
}
// Only fetched when enabled — same "sidebar badge, super-admin only" shape as usePendingBookingsCount.
export function useAutoReassignIssuesCount(enabled = true) {
  return useQuery({
    queryKey: ['auto-reassign-issues', 'count'],
    queryFn: () => bookingService.countAutoReassignIssues(),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    select: (data: any) => data?.open ?? 0,
  })
}
export function useResolveAutoReassignIssue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof bookingService.resolveAutoReassignIssue>[0]) => bookingService.resolveAutoReassignIssue(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auto-reassign-issues'] }),
  })
}

// ── Auto-reassign history ────────────────────────────────────────────────────
export function useAutoReassignHistory(p?: Parameters<typeof bookingService.listAutoReassignHistory>[0]) {
  return useQuery({ queryKey: ['auto-reassign-history', 'list', p], queryFn: () => bookingService.listAutoReassignHistory(p), staleTime: 30_000 })
}
